import {Card, Grid, Group, Loader, SimpleGrid, Stack, Text, Title} from '@mantine/core'
import {
    IconBuildingCommunity,
    IconCoin,
    IconDiamond,
    IconPercentage,
    IconReportMoney,
    IconTargetArrow,
    IconUsers,
} from '@tabler/icons-react'
import {useQuery} from '@tanstack/react-query'
import {createFileRoute, Link} from '@tanstack/react-router'
import {
    Bar,
    BarChart,
    CartesianGrid,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'

import {getOrganization, getOrganizationDashboard} from 'src/api/client/api'
import type {GiftCommitmentsData} from 'src/api/client/models/analytics'
import {useCapacityBucketVerticalBarShape} from 'src/components/analytics/capacity-bucket-vertical-bar-shape'
import DonorPyramid from 'src/components/analytics/donor-pyramid'
import {GiftCommitmentsTable} from 'src/components/analytics/gift-commitments'
import {useProspectNavigation} from 'src/components/analytics/use-prospect-navigation'
import {useScenarioPlanning} from 'src/components/scenario/scenario-context'
import {SectionCard} from 'src/components/section-card'
import {getStoredUser} from 'src/lib/auth'
import {CARTESIAN_GRID_PROPS, CHART_COLORS} from 'src/lib/chart-colors'
import {formatCurrency, formatNumber} from 'src/lib/format'
import {denominator} from 'src/lib/math'

export const Route = createFileRoute('/_authed/organizations/$orgId/')({
    component: DashboardPage,
})

type DashboardStatItem = {
    label: string
    value: string
    icon: React.ComponentType<{size?: number; style?: React.CSSProperties}>
}

/** Desktop (`sm+`): 2–4 cards per row. */
function desktopStatRowPattern(statCount: number): number[] {
    switch (statCount) {
        case 1:
            return [1]
        case 2:
            return [2]
        case 3:
            return [3]
        case 4:
            return [4]
        case 5:
            return [3, 2]
        case 6:
            return [3, 3]
        case 7:
            return [4, 3]
        case 8:
            return [4, 4]
        default:
            return [statCount]
    }
}

function desktopSpanForStatIndex(index: number, rowPattern: number[]): number {
    let start = 0
    for (const rowLen of rowPattern) {
        if (index < start + rowLen) {
            return 12 / rowLen
        }
        start += rowLen
    }
    return 12
}

/** Mobile: 2 columns; last item spans full width when the total count is odd. */
function mobileSpanForStatIndex(index: number, total: number): number {
    const lastSpansFull = index === total - 1 && total % 2 === 1
    return lastSpansFull ? 12 : 6
}

function StatCard({
    label,
    value,
    icon: Icon,
}: {
    label: string
    value: string
    icon: React.ComponentType<{size?: number; style?: React.CSSProperties}>
}) {
    return (
        <Card padding="md" style={{borderLeft: '3px solid var(--mantine-color-green-6)'}}>
            <Group gap="sm">
                <Icon size={24} style={{color: CHART_COLORS.primary}} />
                <Stack gap={2}>
                    <Text size="xs" c="dimmed">
                        {label}
                    </Text>
                    <Text fw={700} size="lg">
                        {value}
                    </Text>
                </Stack>
            </Group>
        </Card>
    )
}

function DashboardPage() {
    const {orgId} = Route.useParams()
    const user = getStoredUser()
    const navigateToProspects = useProspectNavigation(orgId)
    const renderCapacityBarShape = useCapacityBucketVerticalBarShape(navigateToProspects)
    const {appliedScenario} = useScenarioPlanning()

    const {data, isLoading, error} = useQuery({
        queryKey: ['dashboard', orgId, appliedScenario],
        queryFn: () => getOrganizationDashboard(orgId, appliedScenario),
    })

    const {data: org} = useQuery({
        queryKey: ['organization', orgId],
        queryFn: () => getOrganization(orgId),
    })

    if (isLoading) {
        return <Loader size="lg" style={{display: 'block', margin: '60px auto'}} />
    }

    if (error) {
        return <div>Error loading dashboard: {(error as Error).message}</div>
    }

    if (!data) return null

    const totalProspects = data.prospectsByCapacity.reduce((s, r) => s + r.prospectCount, 0)
    const totalLifetimeGiving = data.lifetimeGivingByCapacity.reduce(
        (s, r) => s + Number(r.totalLifetimeGiving),
        0,
    )
    const lifetimeGivingChartData = data.lifetimeGivingByCapacity.map((r) => ({
        capacityBucket: r.capacityBucket,
        totalLifetimeGiving: Number(r.totalLifetimeGiving),
    }))
    const topTierCount =
        data.prospectsByCapacity.find((r) => r.capacityBucket === '$1,000,000+')?.prospectCount ?? 0
    const maxProspectCountForCapacityChart = Math.max(
        0,
        ...data.prospectsByCapacity.map((r) => r.prospectCount),
    )
    const prospectCountSqrtDomain: [number, number | string] =
        maxProspectCountForCapacityChart > 0 ? [0, 'dataMax'] : [0, 1]

    const secondaryStats: DashboardStatItem[] = []
    if (data.shareOfPurse != null && data.shareOfPurse !== '') {
        secondaryStats.push({
            label: 'Untapped Potential',
            value: `$1 : $${denominator(Number(data.shareOfPurse))}`,
            icon: IconPercentage,
        })
    }
    if (data.secInsiderCount > 0) {
        secondaryStats.push({
            label: 'Potential SEC Insiders',
            value: formatNumber(data.secInsiderCount),
            icon: IconReportMoney,
        })
    }
    if (data.foundationProspectCount > 0) {
        secondaryStats.push({
            label: 'Potential Foundation Prospects',
            value: formatNumber(data.foundationProspectCount),
            icon: IconBuildingCommunity,
        })
    }

    const primaryStats: DashboardStatItem[] = [
        {
            label: 'Total Prospects',
            value: formatNumber(totalProspects),
            icon: IconUsers,
        },
        {
            label: 'Lifetime Giving',
            value: formatCurrency(totalLifetimeGiving),
            icon: IconCoin,
        },
        {
            label: 'Campaign Goal',
            value: org ? formatCurrency(org.campaignGoal) : '—',
            icon: IconTargetArrow,
        },
        {
            label: '$1M+ Prospects',
            value: formatNumber(topTierCount),
            icon: IconDiamond,
        },
    ]
    const dashboardStats = [...primaryStats, ...secondaryStats]
    const desktopRowPattern = desktopStatRowPattern(dashboardStats.length)

    const giftCommitmentsData: GiftCommitmentsData = {
        campaignGoal: org?.campaignGoal ?? 0,
        rows: data.campaignGap.map((r) => ({
            giftSize: r.giftSize,
            numberRequired: r.numberRequired,
            numberIdentified: r.numberIdentified,
            sortOrder: r.sortOrder,
        })),
    }

    return (
        <Stack gap="xl">
            <div>
                <Title order={2} fw={700} c="brand.9">
                    Welcome back, {user?.name?.split(' ')[0]}
                </Title>
                <Text c="dimmed" mt={4}>
                    Here's an overview of {org?.name}'s donor landscape.
                </Text>
            </div>

            <Grid gutter="md">
                {dashboardStats.map((stat, index) => (
                    <Grid.Col
                        key={stat.label}
                        span={{
                            base: mobileSpanForStatIndex(index, dashboardStats.length),
                            sm: desktopSpanForStatIndex(index, desktopRowPattern),
                        }}
                    >
                        <StatCard label={stat.label} value={stat.value} icon={stat.icon} />
                    </Grid.Col>
                ))}
            </Grid>

            <SimpleGrid cols={{base: 1, md: 2}} spacing="lg">
                <SectionCard title="Prospects by Capacity Level">
                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart
                            data={data.prospectsByCapacity}
                            margin={{top: 24, right: 8, left: 8, bottom: 60}}
                        >
                            <CartesianGrid {...CARTESIAN_GRID_PROPS} />
                            <XAxis
                                dataKey="capacityBucket"
                                angle={-35}
                                textAnchor="end"
                                height={80}
                                interval={0}
                            />
                            <YAxis
                                type="number"
                                scale="sqrt"
                                domain={prospectCountSqrtDomain}
                                allowDecimals={false}
                                tickFormatter={(v: number) => formatNumber(v)}
                            />
                            <Tooltip
                                formatter={(value: number) => [formatNumber(value), 'Count']}
                                labelFormatter={(label) => label}
                            />
                            <Bar
                                dataKey="prospectCount"
                                fill={CHART_COLORS.secondary}
                                radius={[4, 4, 0, 0]}
                                isAnimationActive={false}
                                shape={renderCapacityBarShape}
                            >
                                <LabelList
                                    dataKey="prospectCount"
                                    position="top"
                                    formatter={(v: number | string) => formatNumber(Number(v))}
                                />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </SectionCard>

                <SectionCard title="Lifetime Giving by Capacity">
                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart
                            data={lifetimeGivingChartData}
                            margin={{top: 24, right: 8, left: 8, bottom: 60}}
                        >
                            <CartesianGrid {...CARTESIAN_GRID_PROPS} />
                            <XAxis
                                dataKey="capacityBucket"
                                angle={-35}
                                textAnchor="end"
                                height={80}
                                interval={0}
                            />
                            <YAxis
                                tickFormatter={(v) =>
                                    v >= 1_000_000 ? `$${v / 1_000_000}M` : `$${v / 1_000}K`
                                }
                            />
                            <Tooltip
                                formatter={(value: number) => [
                                    formatCurrency(value),
                                    'Lifetime Giving',
                                ]}
                                labelFormatter={(label) => label}
                            />
                            <Bar
                                dataKey="totalLifetimeGiving"
                                fill={CHART_COLORS.secondary}
                                radius={[4, 4, 0, 0]}
                                isAnimationActive={false}
                                shape={renderCapacityBarShape}
                            >
                                <LabelList
                                    dataKey="totalLifetimeGiving"
                                    position="top"
                                    formatter={(v: number) =>
                                        v >= 1_000_000
                                            ? `$${(v / 1_000_000).toFixed(1)}M`
                                            : `$${Math.round(v / 1_000)}K`
                                    }
                                />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </SectionCard>
            </SimpleGrid>

            <SectionCard
                title={
                    <Group justify="space-between">
                        <Text fw={600} size="lg">
                            Donor Pyramid
                        </Text>
                        <Link
                            to="/organizations/$orgId/gap-analysis"
                            params={{orgId}}
                            style={{
                                color: CHART_COLORS.secondary,
                                fontWeight: 500,
                                fontSize: 14,
                                textDecoration: 'none',
                            }}
                        >
                            View Gap Analysis
                        </Link>
                    </Group>
                }
            >
                <SimpleGrid cols={{base: 1, lg: 2}} spacing={{base: 'lg', lg: 'xl'}}>
                    <Stack gap="sm" align="center">
                        <DonorPyramid rows={data.campaignGap} />
                    </Stack>
                    <Stack gap="md">
                        <Stack gap={4}>
                            <Text fw={600} size="sm">
                                Gift table
                            </Text>
                            <Text size="sm" c="dimmed">
                                Required prospects vs. identified prospects by gift level.
                                {org ? (
                                    <>
                                        {' '}
                                        Campaign goal{' '}
                                        <Text span fw={600}>
                                            {formatCurrency(org.campaignGoal)}
                                        </Text>
                                        .
                                    </>
                                ) : null}
                            </Text>
                        </Stack>
                        <GiftCommitmentsTable data={giftCommitmentsData} maw="100%" />
                    </Stack>
                </SimpleGrid>
            </SectionCard>
        </Stack>
    )
}
